import { useCallback, useEffect, useState } from 'react'
import {
  Button,
  Drawer,
  Input,
  InputNumber,
  Modal,
  Popconfirm,
  Progress,
  Pagination,
  Segmented,
  Space,
  Switch,
  Table,
  Tabs,
  Tag,
  Typography,
  message,
} from 'antd'
import {
  CheckCircleOutlined,
  CloseCircleOutlined,
  DeleteOutlined,
  ExperimentOutlined,
  PlayCircleOutlined,
  StarFilled,
  StarOutlined,
} from '@ant-design/icons'
import { adminApi, adminLongRunningApi, formatApiError, PAGE_SIZE, type PageResult } from '../../api'
import { ChatMarkdown } from '../../components/ChatMarkdown'
import { ChatRagDebugPanel, type RagDebugPayload } from '../../components/ChatRagDebugPanel'
import { ChatSourceRefList, type SourceRef } from '../../components/ChatSourceRefList'
import { KNOWLEDGE_SCOPE_OPTIONS_SHORT } from '../../constants/knowledgeScope'
import { formatDate, PageHeader, listPagination, tablePagination, usePagedQuery, useServerPagination } from '../../components/ui'
import { GoldDocEditor, type GoldDoc } from '../../components/GoldDocEditor'
import RetrievalEvalPanel from './RetrievalEvalPanel'

const { Text, Paragraph } = Typography

type BatchSummary = {
  total: number
  answer_changed: number
  hit_improved: number
  avg_baseline_response_ms: number
  avg_candidate_response_ms: number
  verdict_baseline: number
  verdict_candidate: number
  verdict_tie: number
  verdict_unset: number
}

type BatchListItem = {
  batch_id: string
  experiment_name: string
  baseline_label: string
  variant_label: string
  config_snapshot_json?: EvalConfigSnapshot | null
  run_count: number
  total_cases: number
  finished_cases: number
  status: string
  progress: number
  status_message: string
  error_message?: string | null
  created_at: string
}

type BatchDetailItem = {
  record_id: number
  eval_run_id: number
  question: string
  baseline_answer: string
  new_answer: string
  baseline_hit_count: number | null
  hit_count: number
  baseline_response_ms: number
  response_ms: number
  answer_changed: boolean
  verdict: string
  baseline_chunk_refs?: SourceRef[] | null
  candidate_chunk_refs?: SourceRef[] | null
}

type BatchDetail = {
  batch_id: string
  experiment_name: string
  baseline_label: string
  variant_label: string
  config_snapshot_json?: EvalConfigSnapshot | null
  summary: BatchSummary
  items: BatchDetailItem[]
  total: number
  page: number
  page_size: number
  status: string
  progress: number
  status_message: string
  total_cases: number
  finished_cases: number
  error_message?: string | null
  created_at: string
}

type CaseFilter = 'all' | 'good' | 'bad' | 'unset'
type AbTarget = 'benchmark' | 'bad' | 'selected'
type EvalPipelineMode = 'harness' | 'agent'

type EvalConfigSnapshot = {
  eval_pipeline_mode?: EvalPipelineMode
  rag_settings?: Record<string, number | boolean>
  overrides?: Record<string, number | boolean>
}

function formatEvalBatchConfig(snap?: EvalConfigSnapshot | null): string {
  if (!snap?.rag_settings) return '—'
  const s = snap.rag_settings
  const mode = snap.eval_pipeline_mode === 'agent' ? 'agent' : 'harness'
  const modeLabel = mode === 'agent' ? 'Agentic' : '自纠错'
  const topK = s.rag_retrieval_top_k ?? '—'
  if (mode === 'agent') {
    return `${modeLabel} · top_k ${topK} · ${s.rag_agent_max_steps ?? '—'} 步`
  }
  return `${modeLabel} · top_k ${topK} · ${s.rag_harness_max_rounds ?? '—'} 轮`
}

type EvalRun = {
  id: number
  batch_id: string
  answer: string
  answer_mode: string
  hit_count: number
  response_ms: number
  chunk_refs?: { item_id: number; title: string }[] | null
  rag_debug_json?: RagDebugPayload | null
  created_at: string
}

type QaRecord = {
  id: number
  user_label: string
  scope: string
  question: string
  answer: string
  answer_mode: string
  response_ms: number
  is_benchmark: boolean
  case_label: string
  notes: string
  deep_think: boolean
  web_search: boolean
  rag_debug_json: RagDebugPayload | null
  chunk_refs?: SourceRef[] | null
  eval_gold_docs?: GoldDoc[]
  eval_top_k?: number
  gold_ready?: boolean
  created_at: string
  latest_eval: EvalRun | null
}

const empty = (p: number): PageResult<QaRecord> => ({
  items: [],
  total: 0,
  page: p,
  page_size: PAGE_SIZE,
})

const ANSWER_MODE_LABEL: Record<string, string> = {
  casual: '寒暄未检索',
  llm: '检索+大模型',
  no_hits: '无命中',
  need_model: '缺对话模型',
  llm_failed: '生成失败',
}

function answerModeTag(mode: string) {
  const label = ANSWER_MODE_LABEL[mode] || mode
  const color =
    mode === 'casual' || mode === 'no_hits'
      ? 'default'
      : mode === 'llm_failed' || mode === 'need_model'
        ? 'error'
        : 'processing'
  return <Tag color={color}>{label}</Tag>
}

function caseTag(label: string) {
  if (label === 'good') return <Tag color="success">Good Case</Tag>
  if (label === 'bad') return <Tag color="error">Bad Case</Tag>
  return <Tag>未分类</Tag>
}

function baselineHitCount(debug: RagDebugPayload | null | undefined): number | null {
  const n = debug?.hit_count
  return typeof n === 'number' ? n : null
}

function ragDebugStepsCount(debug: RagDebugPayload | null | undefined): number {
  const sc = (debug as { steps_count?: number } | null)?.steps_count
  if (typeof sc === 'number') return sc
  return debug?.steps?.length ?? 0
}

function batchStatusTag(status: string) {
  if (status === 'running') return <Tag color="processing">运行中</Tag>
  if (status === 'pending') return <Tag color="default">排队中</Tag>
  if (status === 'error') return <Tag color="error">失败</Tag>
  return <Tag color="success">已完成</Tag>
}

export default function ChatQaEval() {
  const [benchmarkOnly, setBenchmarkOnly] = useState(false)
  const [caseFilter, setCaseFilter] = useState<CaseFilter>('all')
  const [keyword, setKeyword] = useState('')
  const [detail, setDetail] = useState<QaRecord | null>(null)
  const [notesDraft, setNotesDraft] = useState('')
  const [runningId, setRunningId] = useState<number | null>(null)
  const [batchRunning, setBatchRunning] = useState(false)
  const [selectedIds, setSelectedIds] = useState<number[]>([])
  const [deleting, setDeleting] = useState(false)
  const [abOpen, setAbOpen] = useState(false)
  const [abName, setAbName] = useState('')
  const [abTarget, setAbTarget] = useState<AbTarget>('benchmark')
  const [abPipelineMode, setAbPipelineMode] = useState<EvalPipelineMode>('harness')
  const [abTopK, setAbTopK] = useState<number | null>(null)
  const [abRounds, setAbRounds] = useState<number | null>(null)
  const [abAgentSteps, setAbAgentSteps] = useState<number | null>(null)
  const [batchList, setBatchList] = useState<BatchListItem[]>([])
  const batchPag = useServerPagination()
  const batchDetailPag = useServerPagination()
  const [batchTotal, setBatchTotal] = useState(0)
  const [batchLoading, setBatchLoading] = useState(false)
  const [batchDetail, setBatchDetail] = useState<BatchDetail | null>(null)
  const [batchDetailLoading, setBatchDetailLoading] = useState(false)
  const [batchDetailOpen, setBatchDetailOpen] = useState(false)
  const [batchListOpen, setBatchListOpen] = useState(false)

  const loadPage = useCallback(
    (p: number, ps: number) => {
      const params: Record<string, unknown> = {
        page: p,
        page_size: ps,
        benchmark_only: benchmarkOnly,
      }
      if (keyword.trim()) params.q = keyword.trim()
      if (caseFilter !== 'all') params.case_label = caseFilter
      return adminApi
        .get<PageResult<QaRecord>>('/admin/chat-qa/records', { params })
        .then((r) => r.data)
        .catch(() => empty(p))
    },
    [benchmarkOnly, keyword, caseFilter],
  )

  const {
    page,
    setPage,
    total,
    items,
    loading,
    reload,
    handlePaginationChange,
    paginationOptions,
  } = usePagedQuery(loadPage)

  useEffect(() => {
    setPage(1)
  }, [benchmarkOnly, caseFilter, keyword, setPage])

  const reloadBatches = useCallback((p = batchPag.page, ps = batchPag.pageSize) => {
    setBatchLoading(true)
    return adminApi
      .get<PageResult<BatchListItem>>('/admin/chat-qa/eval/batches', {
        params: { page: p, page_size: ps },
      })
      .then((r) => {
        setBatchList(r.data.items)
        setBatchTotal(r.data.total)
      })
      .catch(() => {
        setBatchList([])
        setBatchTotal(0)
      })
      .finally(() => setBatchLoading(false))
  }, [batchPag.page, batchPag.pageSize])

  useEffect(() => {
    if (batchListOpen) void reloadBatches()
  }, [batchListOpen, batchPag.page, batchPag.pageSize, reloadBatches])

  const openBatchList = useCallback(() => {
    setBatchListOpen(true)
    batchPag.resetPage()
  }, [batchPag.resetPage])

  const openBatchDetail = useCallback((batchId: string, p = batchDetailPag.page, ps = batchDetailPag.pageSize, silent = false) => {
    setBatchDetailOpen(true)
    if (!silent) setBatchDetailLoading(true)
    adminApi
      .get<BatchDetail>(`/admin/chat-qa/eval/batches/${batchId}`, {
        params: { page: p, page_size: ps },
      })
      .then((r) => setBatchDetail(r.data))
      .catch((e) => message.error(formatApiError(e)))
      .finally(() => {
        if (!silent) setBatchDetailLoading(false)
      })
  }, [batchDetailPag.page, batchDetailPag.pageSize])

  useEffect(() => {
    const listRunning =
      batchListOpen &&
      batchList.some((b) => b.status === 'pending' || b.status === 'running')
    const detailRunning =
      batchDetailOpen &&
      batchDetail != null &&
      (batchDetail.status === 'pending' || batchDetail.status === 'running')
    if (!listRunning && !detailRunning) return
    const t = window.setInterval(() => {
      if (batchListOpen) void reloadBatches()
      if (detailRunning && batchDetail) {
        openBatchDetail(batchDetail.batch_id, batchDetailPag.page, batchDetailPag.pageSize, true)
      }
    }, 3000)
    return () => window.clearInterval(t)
  }, [
    batchList,
    batchListOpen,
    batchDetail,
    batchDetailOpen,
    batchDetailPag.page,
    batchDetailPag.pageSize,
    batchPag.page,
    batchPag.pageSize,
    openBatchDetail,
    reloadBatches,
  ])

  const setVerdict = (runId: number, verdict: 'baseline' | 'candidate' | 'tie' | '') => {
    if (!batchDetail) return
    adminApi
      .patch(`/admin/chat-qa/eval/runs/${runId}/verdict`, { verdict })
      .then(() => openBatchDetail(batchDetail.batch_id, batchDetailPag.page, batchDetailPag.pageSize))
      .catch((e) => message.error(formatApiError(e)))
  }

  const patchRecord = (id: number, body: Record<string, unknown>, okMsg: string) =>
    adminApi
      .patch(`/admin/chat-qa/records/${id}`, body)
      .then((r) => {
        message.success(okMsg)
        void reload(page)
        if (detail?.id === id) setDetail(r.data as QaRecord)
        return r.data as QaRecord
      })
      .catch((e) => message.error(formatApiError(e)))

  const afterDelete = (deletedIds: number[]) => {
    const idSet = new Set(deletedIds)
    setSelectedIds((prev) => prev.filter((id) => !idSet.has(id)))
    if (detail && idSet.has(detail.id)) setDetail(null)
    void reload(page)
  }

  const doDeleteRecords = (ids: number[]) => {
    if (!ids.length || deleting) return Promise.resolve()
    setDeleting(true)
    const req =
      ids.length === 1
        ? adminApi.delete<{ ok: boolean; deleted: number }>(`/admin/chat-qa/records/${ids[0]}`)
        : adminApi.post<{ deleted: number; requested: number }>('/admin/chat-qa/records/batch-delete', {
            record_ids: ids,
          })
    return req
      .then((r) => {
        const n = ids.length === 1 ? 1 : (r.data.deleted ?? 0)
        message.success(`已删除 ${n} 条`)
        afterDelete(ids)
      })
      .catch((e) => message.error(formatApiError(e)))
      .finally(() => setDeleting(false))
  }

  const confirmDeleteRecords = (ids: number[], title: string) => {
    if (!ids.length || deleting) return
    Modal.confirm({
      title,
      content: `将删除 ${ids.length} 条评测记录及其全部重跑历史，且不可恢复。`,
      okText: '删除',
      okButtonProps: { danger: true },
      onOk: () => doDeleteRecords(ids),
    })
  }

  const toggleBenchmark = (row: QaRecord, on: boolean) => {
    void patchRecord(row.id, { is_benchmark: on }, on ? '已标为评测' : '已取消评测标记')
  }

  const setCaseLabel = (row: QaRecord, label: '' | 'good' | 'bad') => {
    const msg =
      label === 'good' ? '已标为 Good Case' : label === 'bad' ? '已标为 Bad Case' : '已清除分类'
    void patchRecord(row.id, { case_label: label }, msg)
  }

  const saveNotes = () => {
    if (!detail) return
    void patchRecord(detail.id, { notes: notesDraft }, '备注已保存')
  }

  const adoptLatestAsBaseline = (row: QaRecord, markGood: boolean) => {
    Modal.confirm({
      title: '采纳为基线？',
      content: markGood
        ? '将把「最近重跑」的回答覆盖当前基线，并标为 Good Case。历史重跑记录仍保留。'
        : '将把「最近重跑」的回答覆盖当前基线。历史重跑记录仍保留。',
      okText: '采纳',
      onOk: () =>
        adminApi
          .post<QaRecord>(`/admin/chat-qa/records/${row.id}/adopt-latest-eval`, null, {
            params: { mark_good: markGood },
          })
          .then((r) => {
            message.success(markGood ? '已采纳为基线并标为 Good Case' : '已采纳为基线')
            setDetail(r.data)
            setNotesDraft(r.data.notes || '')
            void reload(page)
          })
          .catch((e) => message.error(formatApiError(e))),
    })
  }

  const runBatchEval = (
    title: string,
    content: string,
    body: {
      benchmark_only?: boolean
      case_label?: 'good' | 'bad'
      record_ids?: number[]
      experiment_name?: string
      rag_overrides?: Record<string, number>
      eval_pipeline_mode?: EvalPipelineMode
    },
    emptyHint: string,
    onDone?: (batchId: string) => void,
  ) => {
    Modal.confirm({
      title,
      content,
      okText: '开始',
      onOk: () => {
        setBatchRunning(true)
        const hide = message.loading('正在提交任务…', 0)
        return adminApi
          .post<{ batch_id: string; total: number; status?: string; message?: string }>(
            '/admin/chat-qa/eval/run',
            body,
          )
          .then((r) => {
            if (r.data.total === 0) {
              message.warning(r.data.message || emptyHint)
              return
            }
            message.success(
              r.data.message ||
                `已提交批次 ${r.data.batch_id}（${r.data.total} 条），请点「实验批次」查看进度`,
            )
            setSelectedIds([])
            void reload(page)
            openBatchList()
            if (r.data.batch_id) {
              openBatchDetail(r.data.batch_id, 1)
            }
            onDone?.(r.data.batch_id)
          })
          .catch((e) => message.error(formatApiError(e, '提交失败')))
          .finally(() => {
            hide()
            setBatchRunning(false)
          })
      },
    })
  }

  const runBenchmarkBatch = () =>
    runBatchEval(
      '重跑全部星标用例？',
      '仅重跑表格中点了「星标」的评测用例（is_benchmark），按当时问句与上下文重跑，耗时较长。',
      { benchmark_only: true },
      '没有星标评测用例，请先在记录上点星标',
    )

  const runBadCaseBatch = () =>
    runBatchEval(
      '重跑全部 Bad Case？',
      '重跑所有已标为 Bad Case 的记录（不要求星标），用于回归验证修复效果。',
      { case_label: 'bad' },
      '没有 Bad Case，请先在记录上标红叉',
    )

  const runSelectedBatch = () => {
    if (!selectedIds.length) return
    runBatchEval(
      `重跑所选 ${selectedIds.length} 条？`,
      '仅重跑当前勾选的记录。',
      { record_ids: selectedIds },
      '未找到所选记录',
    )
  }

  const startAbExperiment = () => {
    const name = abName.trim()
    if (!name) {
      message.warning('请填写实验名称')
      return
    }
    if (abTarget === 'selected' && !selectedIds.length) {
      message.warning('请先勾选要评测的记录')
      return
    }
    const rag_overrides: Record<string, number> = {}
    if (abTopK != null && abTopK > 0) rag_overrides.rag_retrieval_top_k = abTopK
    if (abPipelineMode === 'harness') {
      if (abRounds != null && abRounds > 0) rag_overrides.rag_harness_max_rounds = abRounds
    } else if (abAgentSteps != null && abAgentSteps > 0) {
      rag_overrides.rag_agent_max_steps = abAgentSteps
    }
    const body: {
      experiment_name: string
      benchmark_only?: boolean
      case_label?: 'bad'
      record_ids?: number[]
      rag_overrides?: Record<string, number>
      eval_pipeline_mode?: EvalPipelineMode
    } = { experiment_name: name, eval_pipeline_mode: abPipelineMode }
    if (Object.keys(rag_overrides).length) body.rag_overrides = rag_overrides
    if (abTarget === 'benchmark') body.benchmark_only = true
    else if (abTarget === 'bad') body.case_label = 'bad'
    else body.record_ids = selectedIds

    setAbOpen(false)
    runBatchEval(
      `A/B 实验「${name}」`,
      'A=当前基线回答（线上快照），B=按所选流水线与 RAG 参数重跑。完成后请在批次对比中逐条裁决。',
      body,
      abTarget === 'benchmark'
        ? '没有星标评测用例'
        : abTarget === 'bad'
          ? '没有 Bad Case'
          : '未找到所选记录',
      (batchId) => openBatchDetail(batchId),
    )
  }

  const applyRerunResult = (record: QaRecord) => {
    if (detail?.id === record.id) {
      setDetail(record)
      setNotesDraft(record.notes || '')
    }
  }

  const runOne = (row: QaRecord) => {
    const id = row.id
    if (runningId != null || batchRunning) return
    setRunningId(id)
    const hide = message.loading(`正在重跑 #${id}…`, 0)
    adminLongRunningApi
      .post<{ total: number; message?: string; record?: QaRecord; eval?: EvalRun }>(
        `/admin/chat-qa/records/${id}/rerun`,
      )
      .then((r) => {
        const data = r.data
        if (!data.total || data.total < 1) {
          message.warning(data.message || '重跑失败')
          return
        }
        const updated = data.record
        if (updated) {
          applyRerunResult(updated)
        }
        const preview = data.eval?.answer?.slice(0, 80) || data.record?.latest_eval?.answer?.slice(0, 80)
        message.success(
          preview ? `重跑完成（命中 ${data.eval?.hit_count ?? data.record?.latest_eval?.hit_count ?? 0}）` : '重跑完成',
        )
        void reload(page)
      })
      .catch((e) => message.error(formatApiError(e, '重跑失败')))
      .finally(() => {
        hide()
        setRunningId(null)
      })
  }

  const openDetail = (row: QaRecord) => {
    adminApi
      .get<QaRecord>(`/admin/chat-qa/records/${row.id}`)
      .then((r) => {
        setDetail(r.data)
        setNotesDraft(r.data.notes || '')
      })
      .catch((e) => message.error(formatApiError(e)))
  }

  const chatAbTab = (
    <>
      <Space wrap style={{ marginBottom: 16 }}>
        <Input.Search
          allowClear
          placeholder="搜索问题关键词"
          style={{ width: 240 }}
          value={keyword}
          onChange={(e) => {
            const v = e.target.value
            setKeyword(v)
            if (!v) setPage(1)
          }}
          onSearch={(v) => {
            setKeyword(v)
            setPage(1)
          }}
        />
        <Segmented
          value={caseFilter}
          onChange={(v) => setCaseFilter(v as CaseFilter)}
          options={[
            { label: '全部分类', value: 'all' },
            { label: 'Good', value: 'good' },
            { label: 'Bad', value: 'bad' },
            { label: '未分类', value: 'unset' },
          ]}
        />
        <span>
          只展示评测 <Switch checked={benchmarkOnly} onChange={setBenchmarkOnly} />
        </span>
        <Button icon={<ExperimentOutlined />} onClick={() => openBatchList()}>
          实验批次
        </Button>
        <Button
          type="primary"
          icon={<ExperimentOutlined />}
          loading={batchRunning}
          disabled={runningId != null || deleting}
          onClick={() => setAbOpen(true)}
        >
          新建 A/B 实验
        </Button>
        <Button
          icon={<PlayCircleOutlined />}
          loading={batchRunning}
          disabled={runningId != null || deleting}
          onClick={runBenchmarkBatch}
        >
          重跑全部星标
        </Button>
        <Button
          loading={batchRunning}
          disabled={runningId != null || deleting}
          onClick={runBadCaseBatch}
        >
          重跑全部 Bad
        </Button>
        <Button
          icon={<PlayCircleOutlined />}
          loading={batchRunning}
          disabled={!selectedIds.length || runningId != null || deleting}
          onClick={runSelectedBatch}
        >
          重跑所选{selectedIds.length ? ` (${selectedIds.length})` : ''}
        </Button>
        <Button
          danger
          icon={<DeleteOutlined />}
          disabled={!selectedIds.length || deleting || batchRunning || runningId != null}
          loading={deleting}
          onClick={() => confirmDeleteRecords(selectedIds, '批量删除评测记录？')}
        >
          批量删除{selectedIds.length ? ` (${selectedIds.length})` : ''}
        </Button>
      </Space>
      <Table
        rowKey="id"
        size="small"
        loading={loading}
        dataSource={items}
        rowSelection={{
          selectedRowKeys: selectedIds,
          onChange: (keys) => setSelectedIds(keys as number[]),
          getCheckboxProps: () => ({
            disabled: deleting || batchRunning || runningId != null,
          }),
        }}
        pagination={tablePagination(page, total, handlePaginationChange, paginationOptions)}
        columns={[
          {
            title: '评测',
            width: 56,
            render: (_, r) => (
              <Button
                type="text"
                icon={r.is_benchmark ? <StarFilled style={{ color: '#faad14' }} /> : <StarOutlined />}
                onClick={() => toggleBenchmark(r, !r.is_benchmark)}
              />
            ),
          },
          {
            title: '分类',
            width: 108,
            render: (_, r) => caseTag(r.case_label),
          },
          {
            title: '用户',
            dataIndex: 'user_label',
            width: 88,
            render: (v: string) => v || <Text type="secondary">匿名</Text>,
          },
          {
            title: '库',
            dataIndex: 'scope',
            width: 72,
            render: (v: string) =>
              KNOWLEDGE_SCOPE_OPTIONS_SHORT.find((o) => o.value === v)?.label || v,
          },
          {
            title: '问题 / 基线回答',
            render: (_, r) => (
              <div>
                <Text strong>{r.question}</Text>
                <Paragraph type="secondary" ellipsis={{ rows: 2 }} style={{ marginBottom: 4 }}>
                  {r.answer}
                </Paragraph>
                <Space size={4} wrap>
                  {answerModeTag(r.answer_mode)}
                  {baselineHitCount(r.rag_debug_json) != null ? (
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      命中 {baselineHitCount(r.rag_debug_json)}
                    </Text>
                  ) : null}
                </Space>
              </div>
            ),
          },
          {
            title: '最近重跑',
            width: 100,
            render: (_, r) =>
              r.latest_eval ? (
                <Space direction="vertical" size={0}>
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    命中 {r.latest_eval.hit_count}
                  </Text>
                  {answerModeTag(r.latest_eval.answer_mode)}
                </Space>
              ) : (
                <Text type="secondary">—</Text>
              ),
          },
          {
            title: '调试',
            width: 64,
            render: (_, r) =>
              ragDebugStepsCount(r.rag_debug_json) > 0 ? (
                <Tag color="blue">{ragDebugStepsCount(r.rag_debug_json)} 步</Tag>
              ) : (
                <Text type="secondary">—</Text>
              ),
          },
          {
            title: '时间',
            dataIndex: 'created_at',
            width: 150,
            render: (v: string) => formatDate(v),
          },
          {
            title: '操作',
            width: 248,
            render: (_, r) => (
              <Space wrap size={[4, 4]}>
                <Button
                  size="small"
                  type={r.case_label === 'good' ? 'primary' : 'default'}
                  icon={<CheckCircleOutlined />}
                  onClick={() => setCaseLabel(r, r.case_label === 'good' ? '' : 'good')}
                />
                <Button
                  size="small"
                  danger={r.case_label === 'bad'}
                  icon={<CloseCircleOutlined />}
                  onClick={() => setCaseLabel(r, r.case_label === 'bad' ? '' : 'bad')}
                />
                <Button size="small" onClick={() => openDetail(r)}>
                  详情
                </Button>
                <Button
                  size="small"
                  loading={runningId === r.id}
                  disabled={runningId != null && runningId !== r.id}
                  onClick={(e) => {
                    e.stopPropagation()
                    runOne(r)
                  }}
                >
                  重跑
                </Button>
                <Popconfirm
                  title="删除该条评测记录？"
                  description="含全部重跑历史，不可恢复"
                  okText="删除"
                  okButtonProps={{ danger: true }}
                  onConfirm={() => doDeleteRecords([r.id])}
                >
                  <Button
                    size="small"
                    danger
                    icon={<DeleteOutlined />}
                    disabled={deleting || batchRunning || runningId != null}
                    onClick={(e) => e.stopPropagation()}
                  />
                </Popconfirm>
              </Space>
            ),
          },
        ]}
      />
      <Drawer
        title="问答评测详情"
        width={820}
        open={detail != null}
        onClose={() => setDetail(null)}
        extra={
          detail ? (
            <Space>
              <Button
                type={detail.case_label === 'good' ? 'primary' : 'default'}
                icon={<CheckCircleOutlined />}
                onClick={() => setCaseLabel(detail, detail.case_label === 'good' ? '' : 'good')}
              >
                Good
              </Button>
              <Button
                danger={detail.case_label === 'bad'}
                icon={<CloseCircleOutlined />}
                onClick={() => setCaseLabel(detail, detail.case_label === 'bad' ? '' : 'bad')}
              >
                Bad
              </Button>
              <Button
                loading={runningId === detail.id}
                disabled={batchRunning || (runningId != null && runningId !== detail.id)}
                onClick={() => runOne(detail)}
              >
                重跑本条
              </Button>
              {detail.latest_eval ? (
                <Button type="primary" onClick={() => adoptLatestAsBaseline(detail, false)}>
                  采纳为基线
                </Button>
              ) : null}
              <Popconfirm
                title="删除该条评测记录？"
                description="含全部重跑历史，不可恢复"
                okText="删除"
                okButtonProps={{ danger: true }}
                onConfirm={() => doDeleteRecords([detail.id])}
              >
                <Button danger icon={<DeleteOutlined />} disabled={deleting}>
                  删除
                </Button>
              </Popconfirm>
            </Space>
          ) : null
        }
      >
        {detail ? (
          <>
            <Space wrap style={{ marginBottom: 12 }}>
              {caseTag(detail.case_label)}
              {answerModeTag(detail.answer_mode)}
              <Text type="secondary">
                {detail.user_label || '匿名'} ·{' '}
                {KNOWLEDGE_SCOPE_OPTIONS_SHORT.find((o) => o.value === detail.scope)?.label}
                {detail.deep_think ? ' · 深度思考' : ''}
                {detail.web_search ? ' · 联网' : ''}
                {detail.response_ms ? ` · ${detail.response_ms} ms` : ''}
                {baselineHitCount(detail.rag_debug_json) != null
                  ? ` · 基线命中 ${baselineHitCount(detail.rag_debug_json)}`
                  : ''}
              </Text>
            </Space>
            <Text strong>问题</Text>
            <Paragraph>{detail.question}</Paragraph>
            <Text strong>基线回答（记录时 / 采纳后更新）</Text>
            <div className="chat-qa-eval-answer">
              <ChatMarkdown content={detail.answer} />
            </div>
            <ChatSourceRefList refs={detail.chunk_refs} title="基线参考来源" />
            {detail.latest_eval ? (
              <>
                <Text strong>
                  最近重跑（{formatDate(detail.latest_eval.created_at)}）
                </Text>
                {detail.latest_eval.answer.trim() !== detail.answer.trim() ? (
                  <Tag color="processing" style={{ marginLeft: 8 }}>
                    与基线不同
                  </Tag>
                ) : (
                  <Tag style={{ marginLeft: 8 }}>与基线一致</Tag>
                )}
                <Space wrap style={{ marginBottom: 8 }}>
                  {answerModeTag(detail.latest_eval.answer_mode)}
                  <Text type="secondary">命中 {detail.latest_eval.hit_count}</Text>
                  <Text type="secondary">{detail.latest_eval.response_ms} ms</Text>
                </Space>
                <div className="chat-qa-eval-answer">
                  <ChatMarkdown content={detail.latest_eval.answer} />
                </div>
                <ChatSourceRefList refs={detail.latest_eval.chunk_refs} title="重跑参考来源" />
                <Space style={{ marginTop: 8 }}>
                  <Button type="primary" onClick={() => adoptLatestAsBaseline(detail, true)}>
                    采纳为基线并标 Good
                  </Button>
                  <Button onClick={() => adoptLatestAsBaseline(detail, false)}>仅采纳为基线</Button>
                </Space>
                {detail.latest_eval.rag_debug_json?.steps?.length ? (
                  <>
                    <Text strong style={{ display: 'block', marginTop: 8 }}>
                      重跑 RAG 调试
                    </Text>
                    <ChatRagDebugPanel debug={detail.latest_eval.rag_debug_json} />
                  </>
                ) : (
                  <Text type="secondary" style={{ display: 'block', marginBottom: 12 }}>
                    该次重跑无调试数据；请重启后端并重新「重跑」以记录链路。
                  </Text>
                )}
              </>
            ) : null}
            <Text strong style={{ display: 'block', marginTop: 8 }}>
              备注
            </Text>
            <Input.TextArea
              rows={2}
              maxLength={512}
              value={notesDraft}
              onChange={(e) => setNotesDraft(e.target.value)}
              placeholder="记录问题现象、期望答案等"
            />
            <Button size="small" style={{ marginTop: 8 }} onClick={saveNotes}>
              保存备注
            </Button>
            {detail ? (
              <GoldDocEditor
                recordId={detail.id}
                scope={detail.scope}
                goldDocs={detail.eval_gold_docs || []}
                topK={detail.eval_top_k ?? 10}
                chunkRefs={detail.chunk_refs}
                onSaved={(next) =>
                  setDetail({
                    ...detail,
                    eval_gold_docs: next.eval_gold_docs,
                    eval_top_k: next.eval_top_k,
                    gold_ready: next.gold_ready,
                  })
                }
              />
            ) : null}
            <Text strong style={{ display: 'block', marginTop: 16 }}>
              RAG 调试链路（记录时）
            </Text>
            {detail.rag_debug_json?.steps?.length ? (
              <ChatRagDebugPanel debug={detail.rag_debug_json} />
            ) : (
              <Text type="secondary">无调试数据（请用新版本对话后产生的记录）</Text>
            )}
          </>
        ) : null}
      </Drawer>

      <Drawer
        title="A/B 实验批次"
        width={720}
        open={batchListOpen}
        onClose={() => setBatchListOpen(false)}
        extra={
          <Button size="small" loading={batchLoading} onClick={() => void reloadBatches()}>
            刷新
          </Button>
        }
      >
        <Table
          rowKey="batch_id"
          size="small"
          loading={batchLoading}
          dataSource={batchList}
          pagination={tablePagination(
            batchPag.page,
            batchTotal,
            batchPag.handlePaginationChange,
            batchPag.paginationOptions,
          )}
          columns={[
            {
              title: '实验名',
              dataIndex: 'experiment_name',
              render: (v: string, r) => v || <Text type="secondary">{r.batch_id}</Text>,
            },
            {
              title: 'B 组流水线',
              width: 200,
              render: (_, r) => (
                <Text type="secondary" style={{ fontSize: 12 }}>
                  {formatEvalBatchConfig(r.config_snapshot_json)}
                </Text>
              ),
            },
            {
              title: '进度',
              width: 120,
              render: (_, r) => `${r.finished_cases}/${r.total_cases || r.run_count}`,
            },
            {
              title: '状态',
              width: 130,
              render: (_, r) => (
                <Space direction="vertical" size={0} style={{ width: '100%' }}>
                  {batchStatusTag(r.status)}
                  {r.status === 'running' || r.status === 'pending' ? (
                    <Progress percent={r.progress} size="small" showInfo={false} />
                  ) : null}
                </Space>
              ),
            },
            {
              title: '时间',
              dataIndex: 'created_at',
              width: 140,
              render: (v: string) => formatDate(v),
            },
            {
              title: '操作',
              width: 72,
              render: (_, r) => (
                <Button size="small" type="link" onClick={() => openBatchDetail(r.batch_id)}>
                  对比
                </Button>
              ),
            },
          ]}
        />
      </Drawer>

      <Modal
        title="新建 A/B 实验"
        open={abOpen}
        onCancel={() => setAbOpen(false)}
        onOk={startAbExperiment}
        okText="开始实验"
        confirmLoading={batchRunning}
      >
        <Space direction="vertical" style={{ width: '100%' }} size="middle">
          <div>
            <Text>实验名称</Text>
            <Input
              placeholder="如：top_k 80 vs 基线"
              value={abName}
              onChange={(e) => setAbName(e.target.value)}
              maxLength={128}
            />
          </div>
          <div>
            <Text>评测范围（B 组重跑对象）</Text>
            <Segmented
              block
              value={abTarget}
              onChange={(v) => setAbTarget(v as AbTarget)}
              options={[
                { label: '全部星标', value: 'benchmark' },
                { label: '全部 Bad', value: 'bad' },
                { label: '当前勾选', value: 'selected' },
              ]}
            />
          </div>
          <div>
            <Text>评测流水线（B 组重跑方式）</Text>
            <Segmented
              block
              value={abPipelineMode}
              onChange={(v) => setAbPipelineMode(v as EvalPipelineMode)}
              options={[
                { label: '自纠错', value: 'harness' },
                { label: 'Agentic', value: 'agent' },
              ]}
            />
            <Text type="secondary" style={{ fontSize: 12, display: 'block', marginTop: 6 }}>
              {abPipelineMode === 'agent'
                ? '多轮意图分析 + 工具检索，与线上 Agentic 对话一致。'
                : '问句改写 + 多轮扩检索 + 答案核对，自纠错模式。'}
            </Text>
          </div>
          <div>
            <Text type="secondary">B 组 RAG 覆盖（留空=与线上一致）</Text>
            <Space wrap>
              <span>
                检索 top_k{' '}
                <InputNumber min={1} max={200} value={abTopK} onChange={setAbTopK} placeholder="默认" />
              </span>
              {(abPipelineMode === 'harness') && (
                <span>
                  自纠错轮次{' '}
                  <InputNumber min={1} max={5} value={abRounds} onChange={setAbRounds} placeholder="默认" />
                </span>
              )}
              {(abPipelineMode === 'agent') && (
                <span>
                  Agentic 最大步数{' '}
                  <InputNumber
                    min={1}
                    max={30}
                    value={abAgentSteps}
                    onChange={setAbAgentSteps}
                    placeholder="默认"
                  />
                </span>
              )}
            </Space>
          </div>
        </Space>
      </Modal>

      <Drawer
        title={batchDetail?.experiment_name ? `A/B：${batchDetail.experiment_name}` : 'A/B 批次对比'}
        width="92%"
        open={batchDetailOpen}
        onClose={() => {
          setBatchDetailOpen(false)
          setBatchDetail(null)
        }}
        loading={batchDetailLoading && !batchDetail}
      >
        {batchDetail ? (
          <>
            <Space wrap style={{ marginBottom: 12 }}>
              <Tag>{batchDetail.batch_id}</Tag>
              {batchDetail.config_snapshot_json ? (
                <Tag color="processing">{formatEvalBatchConfig(batchDetail.config_snapshot_json)}</Tag>
              ) : null}
              {batchStatusTag(batchDetail.status)}
              {(batchDetail.status === 'running' || batchDetail.status === 'pending') && (
                <Progress
                  percent={batchDetail.progress}
                  style={{ width: 160, marginBottom: 0 }}
                  size="small"
                />
              )}
              <Text type="secondary">
                进度 {batchDetail.finished_cases}/{batchDetail.total_cases || batchDetail.summary.total} 条
              </Text>
              {batchDetail.status === 'done' ? (
                <>
                  <Tag>回答变化 {batchDetail.summary.answer_changed}</Tag>
                  <Tag>命中提升 {batchDetail.summary.hit_improved}</Tag>
                  <Tag color="blue">A 胜 {batchDetail.summary.verdict_baseline}</Tag>
                  <Tag color="green">B 胜 {batchDetail.summary.verdict_candidate}</Tag>
                  <Tag>平 {batchDetail.summary.verdict_tie}</Tag>
                  <Tag>未裁决 {batchDetail.summary.verdict_unset}</Tag>
                </>
              ) : null}
            </Space>
            {batchDetail.error_message ? (
              <Typography.Paragraph type="danger">{batchDetail.error_message}</Typography.Paragraph>
            ) : null}
            {batchDetail.items.map((r) => (
              <div
                key={r.eval_run_id}
                style={{
                  marginBottom: 20,
                  padding: 16,
                  border: '1px solid #e8ecf0',
                  borderRadius: 12,
                  background: r.answer_changed ? '#fafcff' : '#fff',
                }}
              >
                <Space wrap style={{ marginBottom: 8 }}>
                  <Text strong>#{r.record_id}</Text>
                  {r.answer_changed ? <Tag color="processing">回答有变化</Tag> : <Tag>回答一致</Tag>}
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    命中 {r.baseline_hit_count ?? '—'} → {r.hit_count} · {r.baseline_response_ms}ms →{' '}
                    {r.response_ms}ms
                  </Text>
                </Space>
                <Text strong style={{ display: 'block', marginBottom: 4 }}>
                  问题
                </Text>
                <Paragraph style={{ whiteSpace: 'pre-wrap' }}>{r.question}</Paragraph>
                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: '1fr 1fr',
                    gap: 16,
                    alignItems: 'start',
                  }}
                >
                  <div>
                    <Tag color="blue" style={{ marginBottom: 8 }}>
                      A 基线
                    </Tag>
                    <div className="chat-qa-eval-answer">
                      <ChatMarkdown content={r.baseline_answer || '（无）'} />
                    </div>
                    <ChatSourceRefList
                      refs={r.baseline_chunk_refs}
                      title="A 参考来源"
                      compact
                    />
                  </div>
                  <div>
                    <Tag color="green" style={{ marginBottom: 8 }}>
                      B 候选
                    </Tag>
                    <div className="chat-qa-eval-answer">
                      <ChatMarkdown content={r.new_answer || '（无）'} />
                    </div>
                    <ChatSourceRefList
                      refs={r.candidate_chunk_refs}
                      title="B 参考来源"
                      compact
                    />
                  </div>
                </div>
                <Space style={{ marginTop: 12 }}>
                  <Text type="secondary">裁决：</Text>
                  <Button
                    size="small"
                    type={r.verdict === 'baseline' ? 'primary' : 'default'}
                    onClick={() => setVerdict(r.eval_run_id, 'baseline')}
                    disabled={batchDetail.status !== 'done'}
                  >
                    A 更好
                  </Button>
                  <Button
                    size="small"
                    type={r.verdict === 'candidate' ? 'primary' : 'default'}
                    onClick={() => setVerdict(r.eval_run_id, 'candidate')}
                    disabled={batchDetail.status !== 'done'}
                  >
                    B 更好
                  </Button>
                  <Button
                    size="small"
                    type={r.verdict === 'tie' ? 'primary' : 'default'}
                    onClick={() => setVerdict(r.eval_run_id, 'tie')}
                    disabled={batchDetail.status !== 'done'}
                  >
                    相当
                  </Button>
                </Space>
              </div>
            ))}
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 8 }}>
              <Pagination
                {...listPagination(
                  batchDetailPag.page,
                  batchDetail.total,
                  (p, ps) => {
                    batchDetailPag.handlePaginationChange(p, ps)
                    openBatchDetail(batchDetail.batch_id, p, ps ?? batchDetailPag.pageSize)
                  },
                  batchDetailPag.paginationOptions,
                )}
              />
            </div>
          </>
        ) : batchDetailLoading ? (
          <Text type="secondary">加载中…</Text>
        ) : null}
      </Drawer>
    </>
  )

  return (
    <div>
      <PageHeader
        title="对话评测"
        description="星标=回归集。对话 AB 重跑大模型回答；检索 AB 仅测召回排序（Recall / rank / nDCG），测试人员可在界面标注 Gold 文档，无需 JSON。"
      />
      <Tabs
        defaultActiveKey="chat"
        items={[
          {
            key: 'chat',
            label: '对话 AB',
            children: chatAbTab,
          },
          {
            key: 'retrieval',
            label: '检索 AB',
            children: <RetrievalEvalPanel />,
          },
        ]}
      />
    </div>
  )
}
